import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

import { useI18n } from "@/lib/i18n";
const COLOR_MAP = {
  "24": "#38BDF8", "27": "#0EA5E9", "32": "#0284C7",
  "40": "#22C55E", "42": "#16A34A", "43": "#15803D",
  "48": "#F59E0B", "49": "#D97706", "50": "#B45309",
  "55": "#EF4444", "58": "#DC2626", "65": "#B91C1C",
  "70": "#8B5CF6", "75": "#7C3AED",
};

export function getSecondaryLabel(product, box, field = "inch") {
  if (!field || field === "none") return "";
  const attrs = product?.attributes || {};
  if (field.startsWith("attr:")) return String(attrs[field.slice(5)] || "");
  if (field === "inch") {
    const value = attrs.inch || product?.inch || box?.inch || "";
    return value ? `${value}"` : "";
  }
  return String(product?.[field] || box?.[field] || "");
}

export function getFaceLabelLayout(box) {
  const dx = Number(box?.dx || 0);
  const dy = Number(box?.dy || 0);
  const dz = Number(box?.dz || 0);
  if (dx >= dy) {
    return {
      width: dx * 0.86, height: dz * 0.82,
      position: [0, 0, dy / 2 + 0.035], rotation: [0, 0, 0], face: "front",
    };
  }
  return {
    width: dy * 0.86, height: dz * 0.82,
    position: [dx / 2 + 0.035, 0, 0], rotation: [0, Math.PI / 2, 0], face: "side",
  };
}

function FaceLabel({ box, product, secondaryField }) {
  const secondary = getSecondaryLabel(product, box, secondaryField);
  const layout = getFaceLabelLayout(box);
  const texture = useMemo(() => {
    if (!box?.sku || typeof document === "undefined") return null;
    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = 320;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";

    const primary = String(box.sku);
    let primarySize = 168;
    ctx.font = `800 ${primarySize}px Arial, sans-serif`;
    while (ctx.measureText(primary).width > 910 && primarySize > 70) {
      primarySize -= 8;
      ctx.font = `800 ${primarySize}px Arial, sans-serif`;
    }
    ctx.lineWidth = Math.max(8, primarySize * 0.08);
    ctx.strokeStyle = "rgba(15,23,42,0.78)";
    ctx.fillStyle = "rgba(248,250,252,0.98)";
    const primaryY = secondary ? 126 : 160;
    ctx.strokeText(primary, 512, primaryY);
    ctx.fillText(primary, 512, primaryY);

    if (secondary) {
      let secondarySize = 72;
      ctx.font = `700 ${secondarySize}px Arial, sans-serif`;
      while (ctx.measureText(secondary).width > 850 && secondarySize > 38) {
        secondarySize -= 4;
        ctx.font = `700 ${secondarySize}px Arial, sans-serif`;
      }
      ctx.lineWidth = Math.max(5, secondarySize * 0.07);
      ctx.strokeStyle = "rgba(15,23,42,0.68)";
      ctx.fillStyle = "rgba(226,232,240,0.98)";
      ctx.strokeText(secondary, 512, 244);
      ctx.fillText(secondary, 512, 244);
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    tex.needsUpdate = true;
    return tex;
  }, [box?.sku, secondary]);

  useEffect(() => () => texture?.dispose(), [texture]);
  if (!texture || !box?.sku || layout.width <= 0 || layout.height <= 0) return null;

  return (
    <mesh position={layout.position} rotation={layout.rotation} renderOrder={4}>
      <planeGeometry args={[layout.width, layout.height]} />
      <meshBasicMaterial map={texture} transparent depthWrite={false} toneMapped={false} polygonOffset polygonOffsetFactor={-2} />
    </mesh>
  );
}

function BoxMesh({
  box, idx, selected, multiSelected, onSelect,
  dragEnabled, onDragStart, onDragMove, onDragEnd,
  selectionMode, product, secondaryField,
}) {
  const color = COLOR_MAP[String(box.inch)] || "#64748B";
  const cx = box.x + box.dx / 2;
  const cy = box.z + box.dz / 2;
  const cz = box.y + box.dy / 2;
  const meshRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handlePointerDown = (e) => {
    e.stopPropagation();
    if (selectionMode) return;
    if (dragEnabled && !box.locked) {
      setDragging(true);
      e.target.setPointerCapture(e.pointerId);
      onDragStart && onDragStart(idx, e);
    } else {
      onSelect(box, e.nativeEvent || e);
    }
  };
  const handlePointerMove = (e) => {
    if (!dragging) return;
    e.stopPropagation();
    onDragMove && onDragMove(idx, e);
  };
  const handlePointerUp = (e) => {
    if (!dragging) return;
    e.stopPropagation();
    setDragging(false);
    try { e.target.releasePointerCapture(e.pointerId); } catch (_) {}
    onDragEnd && onDragEnd(idx, e);
  };
  const showSelected = selected || dragging;
  const showMulti = multiSelected && !selected;

  return (
    <group position={[cx, cy, cz]}>
      <mesh
        ref={meshRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <boxGeometry args={[box.dx, box.dz, box.dy]} />
        <meshStandardMaterial
          color={color}
          transparent={showSelected || showMulti}
          opacity={showSelected ? 0.6 : showMulti ? 0.75 : 1}
          emissive={showSelected || showMulti ? color : "#000"}
          emissiveIntensity={showSelected ? 0.4 : showMulti ? 0.3 : 0}
        />
      </mesh>
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(box.dx, box.dz, box.dy)]} />
        <lineBasicMaterial color={dragging ? "#FACC15" : showMulti ? "#F97316" : "#0F172A"} linewidth={dragging || showMulti ? 2 : 1} />
      </lineSegments>
      <FaceLabel box={box} product={product} secondaryField={secondaryField} />
    </group>
  );
}

function PalletBase({ L, W, H }) {
  return (
    <>
      <mesh position={[L / 2, -3, W / 2]}>
        <boxGeometry args={[L, 6, W]} />
        <meshStandardMaterial color="#7C4A24" />
      </mesh>
      <mesh position={[L / 2, H / 2, W / 2]}>
        <boxGeometry args={[L, H, W]} />
        <meshBasicMaterial color="#2563EB" transparent opacity={0.06} depthWrite={false} />
      </mesh>
      <lineSegments position={[L / 2, H / 2, W / 2]}>
        <edgesGeometry args={[new THREE.BoxGeometry(L, H, W)]} />
        <lineBasicMaterial color="#2563EB" />
      </lineSegments>
    </>
  );
}

function CameraRig({ view, L, W, H, controlsRef }) {
  const { camera } = useThree();
  useEffect(() => {
    let pos;
    if (view === "top") pos = [L / 2, Math.max(L, W) * 2.2, W / 2 + 0.01];
    else if (view === "front") pos = [L / 2, H / 2, W * 2.5];
    else if (view === "side") pos = [L * 2.5, H / 2, W / 2];
    else pos = [L * 1.6, H * 1.6, W * 1.6];
    camera.position.set(...pos);
    camera.lookAt(L / 2, H / 2, W / 2);
    if (controlsRef.current) {
      controlsRef.current.target.set(L / 2, H / 2, W / 2);
      controlsRef.current.update();
    }
  }, [view, L, W, H, camera, controlsRef]);
  return null;
}

function useDragState({ pallet, onDropBox }) {
  const [dragIdx, setDragIdx] = useState(null);
  const dragStart = useRef(null);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const { camera, gl } = useThree();
  const pointerHit = (e, planeY) => {
    const { clientX, clientY } = e.nativeEvent || e;
    const rect = gl.domElement.getBoundingClientRect();
    raycaster.setFromCamera({ x: ((clientX-rect.left)/rect.width)*2-1, y: -((clientY-rect.top)/rect.height)*2+1 }, camera);
    const hit = new THREE.Vector3();
    return raycaster.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0,1,0), -planeY), hit) ? hit : null;
  };
  const handleStart = (idx, e) => {
    const b = pallet.boxes[idx], hit = pointerHit(e, b.z);
    setDragIdx(idx);
    dragStart.current = { planeY:b.z, offsetX: hit ? b.x-(hit.x-b.dx/2) : 0, offsetY: hit ? b.y-(hit.z-b.dy/2) : 0 };
  };
  const handleMove = (idx, e) => {
    if (dragIdx == null || !dragStart.current) return;
    const hit = pointerHit(e, dragStart.current.planeY);
    if (!hit) return;
    const b = pallet.boxes[idx];
    onDropBox?.({ preview:true, idx, x:Math.max(0, hit.x-b.dx/2+dragStart.current.offsetX), y:Math.max(0, hit.z-b.dy/2+dragStart.current.offsetY), z:dragStart.current.planeY });
  };
  const handleEnd = (idx) => {
    if (dragIdx == null) return;
    onDropBox?.({ preview:false, idx });
    setDragIdx(null); dragStart.current = null;
  };
  return { dragIdx, handleStart, handleMove, handleEnd };
}

function AreaSelector({ enabled, boxes, isBoxVisible, onAreaSelect, onRectChange }) {
  const { camera, gl } = useThree();

  useEffect(() => {
    const el = gl.domElement;

    if (!enabled) {
      onRectChange?.(null);
      return;
    }

    let start = null;
    let additive = false;
    let pointerId = null;

    const point = (e) => {
      const rect = el.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        rect,
      };
    };

    const down = (e) => {
      if (e.button !== 0) return;

      const p = point(e);
      start = { x: p.x, y: p.y };
      additive = !!(e.ctrlKey || e.shiftKey || e.metaKey);
      pointerId = e.pointerId;

      onRectChange?.({
        left: p.x,
        top: p.y,
        width: 0,
        height: 0,
      });

      try { el.setPointerCapture(e.pointerId); } catch (_) {}

      e.preventDefault();
      e.stopPropagation();
    };

    const move = (e) => {
      if (!start) return;

      const p = point(e);

      onRectChange?.({
        left: Math.min(start.x, p.x),
        top: Math.min(start.y, p.y),
        width: Math.abs(p.x - start.x),
        height: Math.abs(p.y - start.y),
      });

      e.preventDefault();
      e.stopPropagation();
    };

    const up = (e) => {
      if (!start) return;

      const p = point(e);
      const left = Math.min(start.x, p.x);
      const right = Math.max(start.x, p.x);
      const top = Math.min(start.y, p.y);
      const bottom = Math.max(start.y, p.y);

      if ((right - left) >= 5 || (bottom - top) >= 5) {
        const rect = el.getBoundingClientRect();
        const selected = [];

        boxes.forEach((b, i) => {
          if (!isBoxVisible(b)) return;

          const v = new THREE.Vector3(
            b.x + b.dx / 2,
            b.z + b.dz / 2,
            b.y + b.dy / 2
          );

          v.project(camera);

          if (v.z < -1 || v.z > 1) return;

          const sx = ((v.x + 1) / 2) * rect.width;
          const sy = ((1 - v.y) / 2) * rect.height;

          if (sx >= left && sx <= right && sy >= top && sy <= bottom) {
            selected.push(i);
          }
        });

        onAreaSelect?.(selected, additive);
      }

      start = null;
      onRectChange?.(null);

      if (pointerId != null) {
        try { el.releasePointerCapture(pointerId); } catch (_) {}
      }

      pointerId = null;
      e.preventDefault();
      e.stopPropagation();
    };

    el.addEventListener("pointerdown", down, true);
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", up, true);
    window.addEventListener("pointercancel", up, true);

    return () => {
      el.removeEventListener("pointerdown", down, true);
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", up, true);
      window.removeEventListener("pointercancel", up, true);
    };
  }, [enabled, boxes, isBoxVisible, onAreaSelect, onRectChange, camera, gl]);

  return null;
}

function Scene({
  boxes, L, W, H, selectedIdx, multiSelSet, isBoxVisible, onSelectBox, setInternalSelectedIdx,
  view, controlsRef,
  dragEnabled, onDropBox, pallet,
  areaSelectEnabled, onAreaSelect, onRectChange,
  products, secondaryField,
}) {
  const drag = useDragState({ pallet, onDropBox });
  const orbitEnabled = drag.dragIdx == null && !areaSelectEnabled;
  return (
    <>
      <color attach="background" args={["#0F172A"]} />
      <ambientLight intensity={0.7} />
      <directionalLight position={[L, H * 2, W]} intensity={0.9} castShadow />
      <directionalLight position={[-L, H, -W]} intensity={0.35} />
      <PalletBase L={L} W={W} H={H} />
      <AreaSelector
        enabled={areaSelectEnabled}
        boxes={boxes}
        isBoxVisible={isBoxVisible}
        onAreaSelect={onAreaSelect}
        onRectChange={onRectChange}
      />
      {boxes.map((b, i) => (
        isBoxVisible(b) && (
          <BoxMesh
            key={i} box={b} idx={i}
            selected={selectedIdx === i}
            multiSelected={multiSelSet.has(i)}
            onSelect={(box, ev) => {
              setInternalSelectedIdx(i);
              onSelectBox && onSelectBox(box, i, ev);
            }}
            dragEnabled={dragEnabled && !areaSelectEnabled}
            selectionMode={areaSelectEnabled}
            onDragStart={drag.handleStart}
            onDragMove={drag.handleMove}
            onDragEnd={drag.handleEnd}
            product={products?.[b.sku]}
            secondaryField={secondaryField}
          />
        )
      ))}
      <gridHelper args={[Math.max(L, W) * 1.5, 20, "#334155", "#1E293B"]} position={[L / 2, 0, W / 2]} />
      <OrbitControls ref={controlsRef} makeDefault enabled={orbitEnabled} target={[L / 2, H / 2, W / 2]} />
      <CameraRig view={view} L={L} W={W} H={H} controlsRef={controlsRef} />
    </>
  );
}

export default function Pallet3D({
  pallet, L, W, H, onSelectBox, selectedIdx: extSelectedIdx, hiddenLayerKeys,
  onDropBox,
  multiSelIdxs,
  onAreaSelect,
  products = {},
}) {
  const { t } = useI18n();
  const [internalSelectedIdx, setInternalSelectedIdx] = useState(null);
  const selectedIdx = extSelectedIdx !== undefined ? extSelectedIdx : internalSelectedIdx;
  const [view, setView] = useState("iso");
  const [dragEnabled, setDragEnabled] = useState(false);
  const [areaSelectEnabled, setAreaSelectEnabled] = useState(false);
  const [marqueeRect, setMarqueeRect] = useState(null);
  const [secondaryField, setSecondaryField] = useState("inch");
  const controlsRef = useRef();

  const boxes = pallet.boxes || [];
  const secondaryOptions = useMemo(() => {
    const opts = [
      ["none", "Alt bilgi yok"], ["inch", "İnç"], ["name", "Ürün Adı"],
      ["brand", "Marka"], ["gtip", "GTIP / HS"], ["barcode", "Barkod"],
    ];
    const keys = new Set();
    Object.values(products || {}).forEach((p) => Object.keys(p?.attributes || {}).forEach((k) => { if (k !== "inch") keys.add(k); }));
    [...keys].sort().forEach((k) => opts.push([`attr:${k}`, k]));
    return opts;
  }, [products]);
  const initialCam = useMemo(() => [L * 1.6, H * 1.6, W * 1.6], [L, W, H]);
  const hiddenSet = useMemo(() => new Set(hiddenLayerKeys || []), [hiddenLayerKeys]);
  const multiSelSet = useMemo(() => new Set(multiSelIdxs || []), [multiSelIdxs]);
  const isBoxVisible = useCallback(
    (b) => !hiddenSet.has(Math.round(b.z)),
    [hiddenSet]
  );

  return (
    <div className="relative w-full h-full bg-[#0F172A] border border-slate-200">
      <div className="absolute top-2 left-2 right-2 z-10 flex flex-wrap gap-1 no-print pr-1">
        {["iso", "top", "front", "side"].map((v) => (
          <button key={v}
            data-testid={`view-${v}`}
            onClick={() => setView(v)}
            className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider border ${view === v ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"}`}>
            {v}
          </button>
        ))}
        <button
          data-testid="toggle-drag"
          onClick={() => setDragEnabled((d) => !d)}
          className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider border ml-2 ${dragEnabled ? "bg-amber-400 text-slate-900 border-amber-400" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"}`}
          title={dragEnabled ? "Sürükle-bırak açık — kapatmak için tıkla" : "Kutuları XY düzleminde sürükle-bırak"}>
          {dragEnabled ? t("pallet3d.drag_on") : t("pallet3d.drag")}
        </button>
        <select
          data-testid="face-label-secondary"
          value={secondaryField}
          onChange={(e) => setSecondaryField(e.target.value)}
          className="ml-2 px-2 py-1 text-[11px] font-mono border border-slate-300 bg-white text-slate-700"
          title="Kutu yüzeyinde SKU altındaki ikinci bilgiyi seç"
        >
          {secondaryOptions.map(([value, label]) => <option key={value} value={value}>Etiket: {label}</option>)}
        </select>
        <button
          data-testid="toggle-area-select"
          onClick={() => {
            setAreaSelectEnabled((v) => {
              const next = !v;
              if (next) setDragEnabled(false);
              if (!next) setMarqueeRect(null);
              return next;
            });
          }}
          className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider border ml-1 ${
            areaSelectEnabled
              ? "bg-emerald-500 text-white border-emerald-500"
              : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
          }`}
        >
          {areaSelectEnabled ? "TOPLU SEÇİM AÇIK" : "TOPLU SEÇİM"}
        </button>
      </div>
      <div className="absolute bottom-2 right-2 z-10 bg-white/90 backdrop-blur px-3 py-2 text-[11px] font-mono border border-slate-300 shadow-sm pointer-events-none">
        <div><span className="text-slate-500">Doluluk:</span> <b>{(pallet.fill_ratio * 100).toFixed(1)}%</b></div>
        <div><span className="text-slate-500">{t("pallet3d.height")}:</span> <b>{pallet.top_height}cm</b></div>
        <div><span className="text-slate-500">{t("pallet3d.weight")}:</span> <b>{pallet.total_weight}kg</b></div>
        <div><span className="text-slate-500">{t("final.pallet3d.box")}:</span> <b>{pallet.box_count}</b></div>
      </div>
      {marqueeRect && (
        <div
          className="absolute z-20 pointer-events-none border-2 border-emerald-400 bg-emerald-400/15"
          style={{
            left: marqueeRect.left,
            top: marqueeRect.top,
            width: marqueeRect.width,
            height: marqueeRect.height,
          }}
        />
      )}

      <Canvas
        data-testid="pallet-3d-canvas"
        style={{ cursor: areaSelectEnabled ? "crosshair" : "default" }}
        camera={{ position: initialCam, fov: 45, near: 0.1, far: 2000 }}
      >
        <Scene
          boxes={boxes}
          L={L} W={W} H={H}
          selectedIdx={selectedIdx}
          multiSelSet={multiSelSet}
          isBoxVisible={isBoxVisible}
          onSelectBox={onSelectBox}
          setInternalSelectedIdx={setInternalSelectedIdx}
          view={view} controlsRef={controlsRef}
          dragEnabled={dragEnabled}
          onDropBox={onDropBox}
          pallet={pallet}
          areaSelectEnabled={areaSelectEnabled}
          onAreaSelect={onAreaSelect}
          onRectChange={setMarqueeRect}
          products={products}
          secondaryField={secondaryField}
        />
      </Canvas>
    </div>
  );
}
